import { ConditionStatus, EntryTypeFieldType } from 'chaika-enums';
import { EntryFieldConditionData, Nullable } from 'chaika-frontend-types/frontend-cms';

import { pickLocaleValue } from '@chaika/i18n/helper';
import { queryClient } from '@store/queryClient';
import { customFieldQueryOptions } from '@template/queries/customFieldQueries';

import { Condition } from '../types';

export interface DentalConditionEntry {
    id: string;
    parentId: Nullable<string>;
    /** Категория верхнего уровня: подпись состояния строится от неё, а не от ближайшего родителя. */
    categoryId: Nullable<string>;
    condition: Nullable<Condition>;
    label: string;
    shortLabel: Nullable<string>;
    isNested: boolean;
}

export function getDentalConditionTree(): EntryFieldConditionData[] {
    const cached = queryClient.getQueryData(
        customFieldQueryOptions.byFieldType(EntryTypeFieldType.DentalTreatment).queryKey,
    );

    return cached?.conditions ?? [];
}

export function getDentalConditionEntry(id: string): DentalConditionEntry | undefined {
    return buildEntries(getDentalConditionTree(), null, null).get(id);
}

/** Активные состояния ветки: из них строится меню выбора у врача. */
export function getActiveChildren(parentId: string): EntryFieldConditionData[] {
    return findConditionNode(getDentalConditionTree(), parentId)?.items.filter(isActive) ?? [];
}

export function getActiveRootCategories(): EntryFieldConditionData[] {
    return getDentalConditionTree().filter(isActive);
}

/**
 * Состояние ветки с нужным цветом. Ищем по цвету, а не по идентификатору:
 * id поддержка не контролирует, а цвет задаёт сама.
 */
export function findConditionIdByCondition(parentId: string, condition: Condition): Nullable<string> {
    return getActiveChildren(parentId).find(item => item.condition === condition)?.id ?? null;
}

function buildEntries(
    conditions: EntryFieldConditionData[],
    parentId: Nullable<string>,
    categoryId: Nullable<string>,
): Map<string, DentalConditionEntry> {
    return conditions.reduce<Map<string, DentalConditionEntry>>((entries, condition) => {
        entries.set(condition.id, {
            id: condition.id,
            parentId,
            categoryId,
            condition: condition.condition,
            label: readLabel(condition),
            shortLabel: readShortLabel(condition),
            isNested: condition.isNested,
        });

        buildEntries(condition.items, condition.id, categoryId ?? condition.id).forEach((entry, id) =>
            entries.set(id, entry),
        );

        return entries;
    }, new Map());
}

function findConditionNode(conditions: EntryFieldConditionData[], id: string): EntryFieldConditionData | undefined {
    for (const condition of conditions) {
        if (condition.id === id) {
            return condition;
        }

        const found = findConditionNode(condition.items, id);

        if (found) {
            return found;
        }
    }

    return undefined;
}

function isActive(condition: EntryFieldConditionData): boolean {
    return condition.status === ConditionStatus.Active;
}

function readLabel(condition: EntryFieldConditionData): string {
    return pickLocaleValue(condition.translations) ?? '';
}

function readShortLabel(condition: EntryFieldConditionData): Nullable<string> {
    return condition.shortTranslations ? (pickLocaleValue(condition.shortTranslations) ?? null) : null;
}
